import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { fetchHealthcareVendorOrders } from "../../actions/healthcareVendorOrdersActions";
import useOrderListFilters from "../../hooks/useOrderListFilters";
import OrderListFilterBar from "../../components/Orders/OrderListFilterBar";
import {
  Card,
  Table,
  Container,
  Row,
  Col,
  Button,
  Badge,
  Form,
  Spinner,
} from "react-bootstrap";
import { Chip, CircularProgress } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { getUserFromLocalStorage } from "../../utilities/user";
import { PaymentBadge } from "../../components/OrderStatusBadges";
import { toast } from "react-toastify";
import { socket } from "../../utilities/socket";
import useVendorOrderRooms from "../../hooks/useVendorOrderRooms";
import { useBranchScope } from "../../context/BranchScopeContext";
import { orderLocationName, vendorOwnerIdOf } from "../../utilities/vendorScope";

const HealthcareOrderTable = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const user = getUserFromLocalStorage();
  // The owner's id: a team member reads the pharmacy's book, not its own.
  const vendorId = vendorOwnerIdOf(user);
  // Branches (4 Oct 2026): the server filters by the branch picked in the
  // navbar; with branches, each row says which branch it is for.
  const { outletIdParam, hasBranches, nameOf } = useBranchScope();
  // Status and payment-method filters live in the URL: the dashboard cards
  // open this list with one set (utilities/orderDeepLinks.js) and the server
  // applies it, so the card's count is this list's length across pages.
  const listFilters = useOrderListFilters();
  const bookOpts = { ...(outletIdParam ? { outletId: outletIdParam } : {}), ...listFilters.filters };

  const { orders, loading, error, currentPage, numberOfPages } = useSelector(
    (state) => state.healthcareVendorOrders
  );

  const [page, setPage] = useState(1);

  // A changed filter restarts at page 1: the page the operator was on may
  // not exist for the narrower list.
  const lastFilterKey = useRef(listFilters.filterKey);
  useEffect(() => {
    if (!vendorId) return;
    const filterChanged = lastFilterKey.current !== listFilters.filterKey;
    lastFilterKey.current = listFilters.filterKey;
    if (filterChanged && page !== 1) {
      setPage(1);
      return;
    }
    dispatch(fetchHealthcareVendorOrders(vendorId, page, 10, bookOpts));
  }, [dispatch, vendorId, page, outletIdParam, listFilters.filterKey]);

  // One join for every order room (the server picks the whole business or
  // this member's branches). The old join is the fallback for an older server.
  useVendorOrderRooms(socket, vendorId, {
    fallback: (s) => s.emit("joinHealthcareRoom", vendorId),
  });

  // Socket.io integration for real-time updates. Named handlers, so leaving
  // this page does not strip another screen's listeners off the shared socket.
  useEffect(() => {
    if (!vendorId) return;
    const refresh = () => dispatch(fetchHealthcareVendorOrders(vendorId, page, 10, bookOpts));
    const onNewOrder = () => {
      refresh();
      toast.info("New healthcare order received!");
    };
    socket.on("newHealthcareOrder", onNewOrder);
    socket.on("orderUpdated", refresh);
    socket.on("healthcareOrderStatusChanged", refresh);

    return () => {
      socket.off("newHealthcareOrder", onNewOrder);
      socket.off("orderUpdated", refresh);
      socket.off("healthcareOrderStatusChanged", refresh);
    };
  }, [vendorId, page, dispatch, outletIdParam, listFilters.filterKey]);

  const getStatusBadge = (status) => {
    const statusMap = {
      PROCESSING: "warning",
      ASSIGNED: "info",
      DISPATCHED: "primary",
      DELIVERED: "success",
      REJECTED: "danger",
    };
    return statusMap[status] || "secondary";
  };

  const getChipStyle = (status) => {
    switch (status) {
      case "PROCESSING":
        return { backgroundColor: "#ff9800", color: "white" };
      case "ASSIGNED":
        return { backgroundColor: "#2196f3", color: "white" };
      case "DISPATCHED":
        return { backgroundColor: "#9c27b0", color: "white" };
      case "DELIVERED":
        return { backgroundColor: "#4caf50", color: "white" };
      case "REJECTED":
        return { backgroundColor: "#f44336", color: "white" };
      default:
        return { backgroundColor: "#757575", color: "white" };
    }
  };

  const handleViewDetails = (orderId) => {
    history.push(`/admin/healthcareOrderDetails/${orderId}`);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" className="mt-3">
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row>
        <Col md="12">
          <Card>
            <Card.Header>
              <Card.Title as="h4">Healthcare Orders</Card.Title>
            </Card.Header>
            <Card.Body>
              <OrderListFilterBar
                delivery={listFilters.delivery}
                method={listFilters.method}
                onDelivery={listFilters.setDelivery}
                onMethod={listFilters.setMethod}
              />
              {orders.length === 0 ? (
                <div className="text-center text-muted py-5">
                  {listFilters.active > 0 ? (
                    <>
                      <h5>No orders match this filter</h5>
                      <p>Choose Any status, or remove the payment method, to see every order</p>
                    </>
                  ) : (
                    <>
                      <h5>No orders yet</h5>
                      <p>Orders from your healthcare services will appear here</p>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Customer</th>
                        {hasBranches && <th>Branch</th>}
                        <th>Items</th>
                        <th>Total</th>
                        <th>Payment</th>
                        <th>Prescription</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => (
                        <tr key={order._id}>
                          <td>
                            <code>{order._id.slice(-8)}</code>
                          </td>
                          <td>
                            {order.customerName || order.client_id?.name || "N/A"}
                          </td>
                          {hasBranches && <td>{orderLocationName(order, nameOf)}</td>}
                          <td>{order.orderItems?.length || 0}</td>
                          <td>
                            <strong>UGX {order.totalPrice?.toLocaleString()}</strong>
                          </td>
                          {/* This table carried NO payment information at all —
                              not even the method. Paid state is derived from the
                              canonical fields via utilities/paymentStatus.js. */}
                          <td>
                            <PaymentBadge order={order} />
                          </td>
                          <td>
                            {order.prescriptionImageUrl ? (
                              <Badge bg="success">Provided</Badge>
                            ) : (
                              <Badge bg="secondary">N/A</Badge>
                            )}
                          </td>
                          <td>
                            <Chip
                              label={order.delivery_status || "PROCESSING"}
                              size="small"
                              style={getChipStyle(order.delivery_status)}
                            />
                          </td>
                          <td>
                            {new Date(order.createdAt).toLocaleDateString()}
                          </td>
                          <td>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleViewDetails(order._id)}
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {/* Pagination */}
                  {numberOfPages > 1 && (
                    <div className="d-flex justify-content-center gap-2 mt-3">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={page === 1}
                        onClick={() => handlePageChange(page - 1)}
                      >
                        Previous
                      </Button>
                      <span className="align-self-center">
                        Page {page} of {numberOfPages}
                      </span>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={page === numberOfPages}
                        onClick={() => handlePageChange(page + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default HealthcareOrderTable;



